import { supabase } from './supabase';

/** Public App Store listing (live, held pre-launch). Every asset QR/link points here. */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6762528411';

export type GrowthCity = {
  id: string;
  name: string | null;
  region: string | null;
  country_code: string | null;
  total_shado_ratings: number | null;
  tier: number | null;
};

export type GrowthVenue = {
  id: string;
  venue_name_user_input: string | null;
  osm_name: string | null;
  venue_type: string | null;
  venue_shado_score: number | null;
  venue_shado_unique_users: number | null;
  venue_shado_rating_count: number | null;
  fsq_popularity: number | null;
  city_id: string | null;
  locality: string | null;
  is_hidden: boolean | null;
};

export const GROWTH_VENUE_COLUMNS =
  'id, venue_name_user_input, osm_name, venue_type, venue_shado_score, venue_shado_unique_users, venue_shado_rating_count, fsq_popularity, city_id, locality, is_hidden';

export function venueDisplayName(v: Pick<GrowthVenue, 'venue_name_user_input' | 'osm_name'>): string {
  return v.venue_name_user_input?.trim() || v.osm_name?.trim() || 'Unnamed venue';
}

export function formatScore(score: number | null): string {
  return score == null ? '—' : score.toFixed(1);
}

/** Cities that have at least one Shado rating, busiest first. */
export async function fetchRatedCities(limit = 80): Promise<GrowthCity[]> {
  try {
    const { data, error } = await supabase
      .from('cities')
      .select('id, name, region, country_code, total_shado_ratings, tier')
      .gt('total_shado_ratings', 0)
      .order('total_shado_ratings', { ascending: false, nullsFirst: false })
      .limit(limit);
    return error ? [] : (data as GrowthCity[]) ?? [];
  } catch {
    return [];
  }
}

/** Rated venues in a city, best score first. Hidden venues excluded client-side. */
export async function fetchCityRankedVenues(cityId: string, limit = 50): Promise<GrowthVenue[]> {
  try {
    const { data, error } = await supabase
      .from('venues')
      .select(GROWTH_VENUE_COLUMNS)
      .eq('city_id', cityId)
      .gte('venue_shado_unique_users', 1)
      .order('venue_shado_score', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (error) return [];
    return ((data as GrowthVenue[]) ?? []).filter((v) => v.is_hidden !== true);
  } catch {
    return [];
  }
}

/** Search rated venues globally by name (for the one-pager / email pickers). */
export async function searchRatedVenues(q: string, limit = 15): Promise<GrowthVenue[]> {
  try {
    const query = supabase
      .from('venues')
      .select(GROWTH_VENUE_COLUMNS)
      .gte('venue_shado_unique_users', 1)
      .order('venue_shado_rating_count', { ascending: false, nullsFirst: false })
      .limit(limit);
    const { data, error } = q.trim()
      ? await query.ilike('venue_name_user_input', `%${q.trim()}%`)
      : await query;
    if (error) return [];
    return ((data as GrowthVenue[]) ?? []).filter((v) => v.is_hidden !== true);
  } catch {
    return [];
  }
}

export type VenueRankContext = {
  venue: GrowthVenue;
  cityName: string | null;
  /** 1-based rank among rated venues in the same city; null when city unknown. */
  rank: number | null;
  totalRatedInCity: number | null;
};

export async function fetchVenueRankContext(venue: GrowthVenue): Promise<VenueRankContext> {
  if (!venue.city_id) {
    return { venue, cityName: venue.locality, rank: null, totalRatedInCity: null };
  }
  const [cityRes, ranked] = await Promise.all([
    supabase.from('cities').select('name').eq('id', venue.city_id).maybeSingle(),
    fetchCityRankedVenues(venue.city_id, 200),
  ]);
  const cityName = cityRes.error ? venue.locality : ((cityRes.data?.name as string | null) ?? venue.locality);
  const idx = ranked.findIndex((v) => v.id === venue.id);
  return {
    venue,
    cityName,
    rank: idx >= 0 ? idx + 1 : null,
    totalRatedInCity: ranked.length > 0 ? ranked.length : null,
  };
}

// ---------------------------------------------------------------------------
// Mockup reviews — persisted in the prod app_config store via the existing
// admin-only RPCs (admin_get_app_config / admin_set_app_config). One jsonb key
// holds every verdict, so no new tables or grants are involved.
// ---------------------------------------------------------------------------

export const MOCKUP_REVIEWS_CONFIG_KEY = 'growth_mockup_reviews';

export type MockupVerdict = {
  verdict: 'approved' | 'needs_work';
  note: string | null;
  at: string;
};

export type MockupReviews = Record<string, MockupVerdict>;

export async function loadMockupReviews(): Promise<{ reviews: MockupReviews; readable: boolean }> {
  try {
    const { data, error } = await supabase.rpc('admin_get_app_config');
    if (error) return { reviews: {}, readable: false };
    const row = (data as { key: string; value: unknown }[] | null)?.find(
      (r) => r.key === MOCKUP_REVIEWS_CONFIG_KEY,
    );
    const value = row?.value;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return { reviews: value as MockupReviews, readable: true };
    }
    return { reviews: {}, readable: true };
  } catch {
    return { reviews: {}, readable: false };
  }
}

export async function saveMockupReviews(reviews: MockupReviews): Promise<string | null> {
  try {
    const { error } = await supabase.rpc('admin_set_app_config', {
      p_key: MOCKUP_REVIEWS_CONFIG_KEY,
      p_value: reviews,
    });
    return error ? error.message : null;
  } catch (e: unknown) {
    return e instanceof Error ? e.message : 'Failed to save review';
  }
}
